import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  Breadcrumb,
  Separator,
  StyledBreadcrumbs,
} from '@/shared/features/header/components/breadcrumbs/breadcrumbs.styled';
import { TBreadcrumb } from '@/shared/features/header/components/breadcrumbs/breadcrumbs.types';
import { PageTitle } from '@/shared/features/header/header.styled';
import { useBreadcrumbs } from '@/shared/features/header/hooks/use-breadcrumbs';

export const Breadcrumbs: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumbs();

  const isBreadcrumbObject = (
    item: TBreadcrumb | React.ReactNode,
  ): item is TBreadcrumb => {
    return (
      item !== null &&
      typeof item === 'object' &&
      !React.isValidElement(item) &&
      'label' in item
    );
  };

  const getPageTitle = (breadcrumb: TBreadcrumb) => {
    if (breadcrumb.localized) {
      return t(`pages:${breadcrumb.label}`);
    }

    return breadcrumb.label;
  };

  const getLabel = (breadcrumb: TBreadcrumb) => {
    let label = breadcrumb.label;

    if (breadcrumb.localized) {
      label = t(`pages:${breadcrumb.label}`);
    }

    return label;
  };

  const showSingleBreadcrumbAsTitle =
    breadcrumbs.length === 1 && isBreadcrumbObject(breadcrumbs[0]);

  return (
    <StyledBreadcrumbs
      aria-label="breadcrumb"
      separator={<Separator>/</Separator>}
    >
      {showSingleBreadcrumbAsTitle ? (
        <PageTitle variant="h1">
          {getPageTitle(breadcrumbs[0] as TBreadcrumb)}
        </PageTitle>
      ) : (
        breadcrumbs.map((breadcrumb, index) => {
          if (React.isValidElement(breadcrumb)) {
            return <Box key={`react-element-${index}`}>{breadcrumb}</Box>;
          }

          if (isBreadcrumbObject(breadcrumb)) {
            const isLastCrumb =
              index === breadcrumbs.length - 1 && breadcrumbs.length > 1;

            return (
              <Breadcrumb
                color={isLastCrumb ? 'textSecondary' : 'textPrimary'}
                variant={isLastCrumb ? 'p7' : 'p6'}
                key={breadcrumb.link || `crumb-${index}`}
                text={getLabel(breadcrumb)}
                onClick={() => breadcrumb.link && navigate(breadcrumb.link)}
              />
            );
          }

          return null;
        })
      )}
    </StyledBreadcrumbs>
  );
};
